import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useAnchorable } from '../../hooks/useAnchorable';
import { appendProtocol, isAnchorLink, isMailToLink, isTelLink } from '../../utils/link';
import { button, buttonContainer } from './Button.css';
import type { TPostButtonProps } from './Button.types';

const getLineHeight = (size?: 'normal' | 'large' | 'small'): string => {
  if (size === 'small') return '32px';
  if (size === 'normal') return '42px';
  if (size === 'large') return '52px';
  return '32px';
};

const getPadding = (size?: 'normal' | 'large' | 'small'): string => {
  if (size === 'small') return '0px 12px';
  if (size === 'normal') return '0px 14px';
  if (size === 'large') return '0px 22px';
  return '0px 14px';
};

const getFontSize = (size?: 'normal' | 'large' | 'small'): string => {
  if (size === 'small') return 'calc(var(--wt-paragraph-font-size) - 2px)';
  if (size === 'normal') return 'var(--wt-paragraph-font-size)';
  if (size === 'large') return 'calc(var(--wt-paragraph-font-size) + 2px)';
  return 'var(--wt-paragraph-font-size)';
};

export const PostButton = (props: TPostButtonProps) => {
  const { element, children } = props;
  const { attrs } = element;
  const { href, size, alignment, fullWidth, customTextColor, customBackgroundColor } = attrs;
  const { anchorAttributes } = useAnchorable(attrs);

  const buttonClassNames = classNames(button, fullWidth && 'w-full');

  // Anchor related
  const { utmParams } = usePostContentContext();
  const hrefWithUtmParams = evaluateAndAppendUTMParams(appendProtocol(href, 'http://'), utmParams);
  const target = attrs.target !== '_self' ? '_blank' : undefined;
  const isClickTrackingDisabled =
    attrs?.clicktracking === 'off' ||
    isAnchorLink(attrs?.href || '') ||
    isMailToLink(attrs?.href || '') ||
    isTelLink(attrs?.href || '');
  const anchorAdditionalAttributes = {};
  if (isClickTrackingDisabled) {
    anchorAdditionalAttributes['data-skip-tracking'] = 'true';
  }

  return (
    <div
      {...anchorAttributes}
      className={buttonContainer}
      style={
        {
          '--button-line-height': getLineHeight(size),
          '--button-padding': getPadding(size),
          '--button-font-size': getFontSize(size),
          '--button-alignment': alignment,
          '--custom-background-color': customBackgroundColor,
          '--custom-text-color': customTextColor,
        } as CSSProperties
      }
    >
      {hrefWithUtmParams ? (
        <a
          href={hrefWithUtmParams}
          className={buttonClassNames}
          type="button"
          target={target}
          {...anchorAdditionalAttributes}
        >
          {children}
        </a>
      ) : (
        <button type="button" className={buttonClassNames}>
          {children}
        </button>
      )}
    </div>
  );
};
